import { useEffect, useRef } from "react";
import { Dialog } from "@mui/material";

import "./styles.css";

/**
 * Sheet excerpt viewer (spreadsheet citations design §4.3): clicking a
 * `sheet` locator opens the cited sheet windowed around the cited rows,
 * highlighted and scrolled into view, instead of downloading the workbook.
 * The host app owns data fetching (its message-validated excerpt endpoint)
 * and passes the result here — same contract style as `handleLink`.
 */

export interface SheetExcerptRow {
  /** Absolute 1-based sheet row (Excel row); null for pre-backfill data. */
  r: number | null;
  cells: string[];
}

export interface SheetExcerpt {
  sheetName: string;
  titleRows: string[];
  header: string[] | null;
  rows: SheetExcerptRow[];
  /** Indices into `rows` of the cited range; null for header-only chunks. */
  highlight: { startIndex: number; endIndex: number } | null;
  totalRows: number;
  truncated: boolean;
  /** Data rows hidden above/below the returned window. */
  window: { before: number; after: number };
}

export interface SheetViewerProps {
  open: boolean;
  /** Source file name (the citation's `source`), used in the dialog name. */
  sourceName: string;
  excerpt?: SheetExcerpt | null;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  /** "Open file" fallback — the host's whole-file open (presigned download). */
  onOpenFile?: () => void;
  /**
   * Live spreadsheet deep link (provider opt-in): the citation's live URL —
   * a Google Sheets editor URL (needs `gid` for the #gid/range fragment) or
   * a SharePoint/OneDrive webUrl (Excel for the web deep-links by sheet
   * name + cell, no gid needed). The viewer builds the location fragment
   * from the excerpt's sheet name and absolute rows.
   */
  liveSheet?: { url: string; gid?: string | number | null };
}

/** 1-based column index -> spreadsheet column letters (1 = A, 27 = AA). */
function colLetter(n: number): string {
  let s = "";
  while (n > 0) {
    s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s || "A";
}

export function SheetViewer({
  open,
  sourceName,
  excerpt,
  loading,
  error,
  onClose,
  onOpenFile,
  liveSheet,
}: SheetViewerProps) {
  const citedRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    // Bring the first cited row into view once the excerpt renders.
    // (scrollIntoView is absent in jsdom — hence the optional call.)
    if (open && excerpt)
      citedRef.current?.scrollIntoView?.({ block: "center" });
  }, [open, excerpt]);

  const hl = excerpt?.highlight ?? null;
  // Live-sheet deep link: open the real spreadsheet scrolled to (and, where
  // supported, highlighting) the cited range; without absolute rows, fall
  // back to just selecting the sheet tab.
  const live = (() => {
    if (!liveSheet) return null;
    const base = liveSheet.url.split("#")[0];
    const a = hl && excerpt ? excerpt.rows[hl.startIndex]?.r : null;
    const b = hl && excerpt ? excerpt.rows[hl.endIndex]?.r : null;
    if (/docs\.google\.com\/spreadsheets\//.test(base)) {
      if (liveSheet.gid == null) return null;
      const colCount =
        excerpt?.header?.length ||
        Math.max(0, ...(excerpt?.rows ?? []).map((row) => row.cells.length));
      const range =
        a != null && b != null && colCount > 0
          ? `&range=A${a}:${colLetter(colCount)}${b}`
          : "";
      return {
        href: `${base}#gid=${liveSheet.gid}${range}`,
        label: "Open in Google Sheets",
      };
    }
    // SharePoint / OneDrive: Excel for the web addresses by sheet NAME +
    // cell via the activeCell query parameter (quotes inside sheet names
    // are doubled). A1 when absolute rows are unknown just selects the tab.
    const sheet = excerpt?.sheetName;
    if (!sheet) return null;
    const cell = `'${String(sheet).replace(/'/g, "''")}'!A${a ?? 1}`;
    const sep = base.includes("?") ? "&" : "?";
    return {
      href: `${base}${sep}web=1&activeCell=${encodeURIComponent(cell)}`,
      label: "Open in Excel",
    };
  })();
  // A sheet's title banner frequently repeats the sheet name — showing both
  // reads as a duplicate, so drop matching title rows from the subtitle.
  const titleRows = (excerpt?.titleRows ?? []).filter(
    (t) =>
      t.trim().toLowerCase() !==
      (excerpt?.sheetName ?? "").trim().toLowerCase(),
  );
  const citedLabel = (() => {
    if (!excerpt || !hl) return "";
    const a = excerpt.rows[hl.startIndex];
    const b = excerpt.rows[hl.endIndex];
    if (a?.r != null && b?.r != null) {
      return b.r !== a.r ? `cited rows ${a.r}–${b.r}` : `cited row ${a.r}`;
    }
    const count = hl.endIndex - hl.startIndex + 1;
    return `${count} cited row${count === 1 ? "" : "s"}`;
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      // Dialog name via aria-label, not labelledby (VoiceOver house rule).
      PaperProps={{
        className: "cs-sheet-viewer",
        "aria-label": `Cited rows from ${sourceName}`,
      }}
    >
      <div className="cs-sheet-viewer__header">
        <h2 className="cs-sheet-viewer__title">{sourceName}</h2>
        {excerpt && (
          <p className="cs-sheet-viewer__subtitle">
            {`Sheet “${excerpt.sheetName}”`}
            {citedLabel ? ` · ${citedLabel}` : ""}
            {` · ${excerpt.totalRows} data row${excerpt.totalRows === 1 ? "" : "s"}`}
            {excerpt.truncated ? " (sheet truncated at the ingestion cap)" : ""}
          </p>
        )}
        {excerpt && titleRows.length > 0 && (
          <p className="cs-sheet-viewer__subtitle cs-sheet-viewer__subtitle--muted">
            {titleRows.join(" — ")}
          </p>
        )}
      </div>
      {loading && (
        <p className="cs-sheet-viewer__status" role="status">
          Loading sheet…
        </p>
      )}
      {!loading && error && (
        <p
          className="cs-sheet-viewer__status cs-sheet-viewer__status--error"
          role="alert"
        >
          {error}
        </p>
      )}
      {!loading && !error && excerpt && (
        <div className="cs-sheet-viewer__scroll">
          {excerpt.window.before > 0 && (
            <p className="cs-sheet-viewer__gap">
              … {excerpt.window.before} more row
              {excerpt.window.before === 1 ? "" : "s"} above
            </p>
          )}
          <table className="cs-sheet-viewer__table">
            <caption className="cs-sheet-viewer__sr-only">
              Rows from sheet {excerpt.sheetName}; cited rows are marked.
            </caption>
            {excerpt.header && (
              <thead>
                <tr>
                  <th scope="col" className="cs-sheet-viewer__rownum">
                    Row
                  </th>
                  {excerpt.header.map((h, i) => (
                    <th scope="col" key={i}>
                      {/* Inner block carries the column-width cap: max-width
                          on table cells in auto layout is not reliable
                          cross-browser. */}
                      <div className="cs-sheet-viewer__cell">{h}</div>
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {excerpt.rows.map((row, i) => {
                const cited =
                  hl != null && i >= hl.startIndex && i <= hl.endIndex;
                return (
                  <tr
                    key={i}
                    ref={cited && i === hl!.startIndex ? citedRef : undefined}
                    className={
                      cited ? "cs-sheet-viewer__row--cited" : undefined
                    }
                  >
                    <th scope="row" className="cs-sheet-viewer__rownum">
                      {row.r ?? ""}
                      {cited && (
                        <span className="cs-sheet-viewer__sr-only"> cited</span>
                      )}
                    </th>
                    {row.cells.map((c, j) => (
                      <td key={j}>
                        <div className="cs-sheet-viewer__cell">{c}</div>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {excerpt.window.after > 0 && (
            <p className="cs-sheet-viewer__gap">
              … {excerpt.window.after} more row
              {excerpt.window.after === 1 ? "" : "s"} below
            </p>
          )}
        </div>
      )}
      <div className="cs-sheet-viewer__footer">
        {live && (
          <a
            className="cs-sheet-viewer__button"
            href={live.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {live.label}
          </a>
        )}
        {onOpenFile && (
          <button
            type="button"
            className="cs-sheet-viewer__button"
            onClick={onOpenFile}
          >
            Open file
          </button>
        )}
        <button
          type="button"
          className="cs-sheet-viewer__button cs-sheet-viewer__button--primary"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </Dialog>
  );
}
